<template>
  <div class="display-viewer" :class="{ 'is-zoomed': zoomed, 'is-showing-graph': !!toDrawGraph }">
    <div id="stage-div" class="stage-div">
    <div class="stage-viewport" ref="viewport">
      <v-stage
        class="overflow-hidden"
        style="border: 1px solid black;"
        ref="stage"
        :config="{width: display.width, height: display.height, draggable: true}"
      >
      <!-- background layer -->
      <v-layer>
        <v-image
          v-if="backgroundImageLoaded"
          :config="{perfectDrawEnabled: false, listening: false, image: backgroundImage}"
        />
        <v-rect
          v-else
          :config="{
              width: display.width,
              height: display.height,
              fill: 'lightgray' ,
              perfectDrawEnabled: false,
              listening: false
            }"
        />
      </v-layer>
      <!-- elements layer -->
      <v-layer>
        <!-- textboxes -->
        <span v-if="display.blobObject.textBox">
          <div v-for="(value, key) in display.blobObject.textBox" :key="'tb' + key">
            <template v-if="isVisible(value)">
            <v-rect :config="getTextBoxRectConfig(value)" />
            <v-text
              :config="getTextBoxTextConfig(value)"
              @mouseenter="showToolTip(value, false)"
              @mouseout="hideToolTip()"
              @click="textBoxPress(value)"
              @tap="textBoxPress(value)"
            />
            </template>
          </div>
        </span>
        <!-- commands -->
        <span v-if="display.blobObject.command">
          <div v-for="(value, key) in display.blobObject.command" :key="'cmd' + key">
            <template v-if="isVisible(value)">
            <div v-if="value.imageWhenFalse && value.imageWhenTrue">
              <v-image
                :config="getCommandImagesConfig(value, 'cmd' + key)"
                @mouseenter="showToolTip(value, false)"
                @mouseout="hideToolTip()"
                @click="commandPress(value)"
                @tap="commandPress(value)"
              />
            </div>
            <div v-else>
              <v-rect :config="getCommandRectConfig(value)" />
              <v-rect :config="getCommandSwitchConfig(value)" />
              <v-text
                :config="getCommandTextConfig(value)"
                @mouseenter="showToolTip(value, false)"
                @mouseout="hideToolTip()"
                @click="commandPress(value)"
                @tap="commandPress(value)"
              />
            </div>
            </template>
          </div>
        </span>
        <!-- graphs -->
        <span v-if="display.blobObject.graph && graphImageLoaded">
          <div v-for="(value, key) in display.blobObject.graph" :key="'gh' + key">
            <v-image
              v-if="isVisible(value)"
              :config="{
                image: graphImage,
                width: value.width,
                height: value.height,
                x: value.positionX,
                y: value.positionY
              }"
              @mouseenter="showToolTip(value, true)"
              @mouseout="hideToolTip()"
              @click="graphPress(value)"
              @tap="graphPress(value)"
            />
          </div>
        </span>
        <!-- extraTags -->
        <span v-if="display.blobObject.extraTag">
          <div v-for="(value, key) in display.blobObject.extraTag" :key="'et' + key">
            <template v-if="isVisible(value)">
            <v-rect :config="getTextBoxRectConfig(value)" />
            <v-text
              :config="getTextBoxTextConfig(value)"
              @mouseenter="showToolTip(value, false, true)"
              @mouseout="hideToolTip()"
              @click="extraTagPress(value)"
              @tap="extraTagPress(value)"
            />
            </template>
          </div>
        </span>
        <!-- graphics -->
        <span v-if="display.blobObject.graphic">
          <div v-for="(value, key) in display.blobObject.graphic" :key="'g' + key">
            <v-image
              v-if="isVisible(value) && value.imageString"
              :config="getGraphicConfig(value)"
              @mouseenter="showToolTip(value, false)"
              @mouseout="hideToolTip()"
            />
          </div>
        </span>
      </v-layer>
      <!-- tooltip layer -->
      <v-layer ref="tooltipLayer">
        <v-rect ref="tooltipRect" :config="toolTipRectConfig" />
        <v-text ref="tooltipText" :config="toolTipTextConfig" />
      </v-layer>
    </v-stage>
    </div>

    <div
      v-if="isMobile"
      class="alert alert-warning mobile-canvas-hint mb-0 mt-1 py-1 px-2"
      role="note"
    >
      {{ $t('project.projectViewer.mobileCanvasHint') }}
    </div>
    </div>

    <div ref="graphViewer" class="graph-viewer">
      <div class="mt-2" v-if="toDrawGraph">
        <b-card border-variant="secondary" body-class="p-2">
          <b-row>
            <b-col sm="5">
              <h4 class="card-title mb-0">{{ $t('project.graphViewer.title') }}</h4>
              <div class="small text-muted">{{ toDrawGraph.toolTipText }}</div>
            </b-col>
            <b-col sm="7" class="d-sm-block">
              <b-button
                type="button"
                variant="outline-dark"
                class="float-right"
                @click="closeGraphViewer()"
              >
                <i class="icon-close"></i>
              </b-button>
              <b-button-toolbar class="float-right">
                <b-form-radio-group
                  class="mr-3"
                  buttons
                  button-variant="outline-secondary"
                  v-model="graphMode"
                >
                  <b-form-radio class="mx-0" value="day">{{ $t('project.graphViewer.day') }}</b-form-radio>
                  <b-form-radio class="mx-0" value="three">{{ $t('project.graphViewer.three') }}</b-form-radio>
                  <b-form-radio class="mx-0" value="custom">{{ $t('project.graphViewer.custom') }}</b-form-radio>
                </b-form-radio-group>
              </b-button-toolbar>
            </b-col>
          </b-row>
          <chartManager
            ref="chartManager"
            :variable="toDrawGraphVariable"
            :projectId="projectId"
            :graphMode="graphMode"
          />
        </b-card>
      </div>
    </div>

    <b-modal ref="modalSetValue" title="Set value" ok-only hide-header>
      <div class="d-block text-center">
        <h4>{{ $t('project.projectViewer.setNewValueModal') }}</h4>
        <h6>{{ tbToSet.toolTipText }}</h6>
      </div>
      <b-input-group>
        <b-form-select
          v-if="getTagSelection(tbToSet) != null"
          v-model.number="tbToSet.newValue"
          :options="getTagSelection(tbToSet)"
          size="sm"
        ></b-form-select>
        <b-form-input v-else v-model.number="tbToSet.newValue" type="number" step="any" size="sm"></b-form-input>
        <b-input-group-append>
          <b-button size="sm" variant="outline-dark" @click="sendNewValue(tbToSet)">
            {{$t('project.projectViewer.sendValueTooltip')}}
            <i class="icon-arrow-right-circle" />
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </b-modal>
  </div>
</template>

<script>
import {
  codeTagVariable,
  getDistance,
  clientPointerRelativeToStage
} from "@/_helpers";
import ChartManager from "@/components/charts/ChartManager";
import { extraTagService } from "@/_services";
import { errorHandler, getTagSelectionInput } from "@/_helpers";

export default {
  name: "DisplayViewer",
  components: {
    chartManager: ChartManager
  },
  props: {
    adminRights: {
      type: Boolean,
      default: false
    },
    display: {
      type: Object,
      default() {
        return {};
      }
    },
    projectId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      tbToSet: {},
      toDrawGraph: null,
      toDrawGraphVariable: null,
      graphImage: null,
      graphImageLoaded: false,
      backgroundImage: null,
      backgroundImageLoaded: false,
      imagesOnOff: new Map(),
      graphMode: "day",
      toolTipRectConfig: {
        cornerRadius: 3,
        fill: "lightyellow",
        visible: false,
        perfectDrawEnabled: false,
        listening: false
      },
      toolTipTextConfig: {
        text: "",
        fontFamily: "Calibri",
        fontSize: 12,
        padding: 5,
        fill: "black",
        visible: false,
        perfectDrawEnabled: false,
        listening: false
      },
      lastDist: undefined,
      lastMid: undefined,
      point: undefined,
      zoomed: false,
      fullscreen: false,
      orgScale: 1,
      isMobile: false
    };
  },
  created() {
    this.updateIsMobile();
    const image = new Image();
    image.onload = () => {
      this.graphImage = image;
      this.graphImageLoaded = true;
    };
    image.src = "img/graph-icon.png";
  },
  mounted() {
    this.setBackgroundImage();
    this.pinchZoomTouchEvent();
    this.pinchDoubleClick();
    this.bindSizeListeners();
    this.$nextTick(() => {
      this.fitStage();
      this.scrollPageToTop();
      requestAnimationFrame(() => this.scrollPageToTop());
    });
  },
  activated() {
    this.bindSizeListeners();
    this.$nextTick(() => {
      this.fitStage();
      this.scrollPageToTop();
      requestAnimationFrame(() => this.scrollPageToTop());
    });
  },
  deactivated() {
    this.unbindSizeListeners();
  },
  beforeDestroy() {
    this.unbindSizeListeners();
  },
  methods: {
    updateIsMobile() {
      this.isMobile =
        (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ||
        window.innerWidth <= 991;
    },
    scrollPageToTop() {
      const cardBody = this.$el && this.$el.closest(".card-body");
      if (cardBody) cardBody.scrollTop = 0;
      const main = this.$el && this.$el.closest(".main");
      if (main) main.scrollTop = 0;
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    },
    onWindowResize() {
      this.updateIsMobile();
      this.fitStage();
    },
    bindSizeListeners() {
      this.unbindSizeListeners();
      this.updateIsMobile();
      window.addEventListener("resize", this.onWindowResize, false);
      window.addEventListener("orientationchange", this.onWindowResize, false);
      document.addEventListener("fullscreenchange", this.onFullscreenChange);
      document.addEventListener("webkitfullscreenchange", this.onFullscreenChange);
      if (typeof ResizeObserver !== "undefined") {
        this.resizeObserver = new ResizeObserver(() => this.fitStage());
        const cardBody = this.$el.closest(".card-body");
        if (cardBody) this.resizeObserver.observe(cardBody);
        if (this.$refs.viewport) this.resizeObserver.observe(this.$refs.viewport);
        if (this.$el) this.resizeObserver.observe(this.$el);
      }
    },
    unbindSizeListeners() {
      window.removeEventListener("resize", this.onWindowResize, false);
      window.removeEventListener("orientationchange", this.onWindowResize, false);
      document.removeEventListener("fullscreenchange", this.onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", this.onFullscreenChange);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    onFullscreenChange() {
      this.fullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
      this.$nextTick(() => {
        requestAnimationFrame(() => this.fitStage(true));
      });
    },
    fullScreenSwitcher() {
      if (this.fullscreen) this.fullscreenOff();
      else this.fullscreenOn();
    },
    fullscreenOn() {
      var elem = this.$el;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
        this.fullscreen = true;
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
        this.fullscreen = true;
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
        this.fullscreen = true;
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
        this.fullscreen = true;
      } else if (elem.webkitSupportsFullscreen) {
        elem.webkitEnterFullScreen();
        this.fullscreen = true;
      }
    },
    fullscreenOff() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        this.fullscreen = false;
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
        this.fullscreen = false;
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
        this.fullscreen = false;
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
        this.fullscreen = false;
      } else if (document.webkitSupportsFullscreen) {
        document.webkitExitFullScreen();
        this.fullscreen = false;
      }
    },
    pinchDoubleClick() {
      if (!this.$refs.stage) return;
      var stage = this.$refs.stage.getStage();
      stage.getContent().addEventListener("dbltap", evt => {
        evt.stopPropagation();
        evt.preventDefault();
        this.doubleClickEvent(stage);
      });
      stage.getContent().addEventListener("dblclick", evt => {
        evt.stopPropagation();
        evt.preventDefault();
        this.doubleClickEvent(stage);
      });
    },
    doubleClickEvent(stage) {
      const oldScale = stage.scaleX();
      const pointer = stage.getPointerPosition();
      const startPos = {
        x: pointer.x / oldScale - stage.x() / oldScale,
        y: pointer.y / oldScale - stage.y() / oldScale
      };

      let newScale = this.orgScale;
      if (!this.zoomed) newScale *= 3;
      stage.scale({ x: newScale, y: newScale });

      const newPosition = {
        x: (pointer.x / newScale - startPos.x) * newScale,
        y: (pointer.y / newScale - startPos.y) * newScale
      };
      if (this.zoomed) stage.position({ x: 0, y: 0 });
      else stage.position(newPosition);
      stage.batchDraw();
      this.zoomed = !this.zoomed;
      stage.draggable(this.zoomed);
    },
    pinchZoomWheelEvent() {
      if (!this.$refs.stage) return;
      var stage = this.$refs.stage.getStage();
      stage.getContent().addEventListener(
        "wheel",
        wheelEvent => {
          wheelEvent.preventDefault();
          const oldScale = stage.scaleX();

          const pointer = stage.getPointerPosition();
          const startPos = {
            x: pointer.x / oldScale - stage.x() / oldScale,
            y: pointer.y / oldScale - stage.y() / oldScale
          };

          const deltaYBounded = !(wheelEvent.deltaY % 1)
            ? Math.abs(Math.min(-10, Math.max(10, wheelEvent.deltaY)))
            : Math.abs(wheelEvent.deltaY);
          const scaleBy = 1.01 + deltaYBounded / 70;
          const newScale =
            wheelEvent.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
          stage.scale({ x: newScale, y: newScale });

          const newPosition = {
            x: (pointer.x / newScale - startPos.x) * newScale,
            y: (pointer.y / newScale - startPos.y) * newScale
          };
          stage.position(newPosition);
          stage.batchDraw();
          this.zoomed = true;
        },
        false
      );
    },
    pinchZoomTouchEvent() {
      if (!this.$refs.stage) return;
      var stage = this.$refs.stage.getStage();
      stage.getContent().addEventListener(
        "touchstart",
        evt => {
          if (evt.touches.length === 1 && !this.zoomed) {
            stage.draggable(false);
          } else {
            stage.draggable(true);
          }
        },
        { passive: true }
      );
      stage.getContent().addEventListener(
        "mousedown",
        () => {
          stage.draggable(true);
        },
        { passive: true }
      );
      stage.getContent().addEventListener(
        "touchmove",
        evt => {
          const t1 = evt.touches[0];
          const t2 = evt.touches[1];

          if (!(t1 && t2)) return;
          evt.preventDefault();
          evt.stopPropagation();

          const dist = getDistance(
            { x: t1.clientX, y: t1.clientY },
            { x: t2.clientX, y: t2.clientY }
          );
          const px = (t1.clientX + t2.clientX) / 2;
          const py = (t1.clientY + t2.clientY) / 2;
          if (!this.lastDist) this.lastDist = dist;
          if (!this.lastMid) this.lastMid = { x: px, y: py };

          const dx = px - this.lastMid.x;
          const dy = py - this.lastMid.y;
          if (dx || dy) {
            stage.position({
              x: stage.x() + dx,
              y: stage.y() + dy
            });
          }

          const delta = dist - this.lastDist;
          if (Math.abs(delta) > 0.5) {
            const oldScale = stage.scaleX();
            const pointer = clientPointerRelativeToStage(px, py, stage);
            const startPos = {
              x: pointer.x / oldScale - stage.x() / oldScale,
              y: pointer.y / oldScale - stage.y() / oldScale
            };
            const scaleBy = 1.01 + Math.abs(delta) / 100;
            const newScale =
              delta < 0 ? oldScale / scaleBy : oldScale * scaleBy;
            stage.scale({ x: newScale, y: newScale });
            stage.position({
              x: (pointer.x / newScale - startPos.x) * newScale,
              y: (pointer.y / newScale - startPos.y) * newScale
            });
            this.zoomed = true;
          }

          stage.batchDraw();
          this.lastDist = dist;
          this.lastMid = { x: px, y: py };
        },
        { passive: false }
      );

      stage.getContent().addEventListener(
        "touchend",
        () => {
          this.lastDist = 0;
          this.lastMid = undefined;
          this.point = undefined;
        },
        false
      );
    },
    fitStage(force) {
      if (this.toDrawGraph && force !== true) return;
      if (!this.$refs.stage) return;
      var stage = this.$refs.stage.getStage();
      var container = this.$refs.viewport || this.$el;
      var isFs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      if (isFs && this.$el) container = this.$el;
      var containerWidth = container.clientWidth;
      var containerHeight = container.clientHeight;
      if (containerWidth < 1 || containerHeight < 1) return;
      if (!this.display.width || !this.display.height) return;

      var scale = Math.min(
        1,
        containerWidth / this.display.width,
        containerHeight / this.display.height
      );
      var stageWidth = this.display.width * scale;
      var stageHeight = this.display.height * scale;
      var scaleDiff = Math.abs(this.orgScale - scale);
      if (
        force !== true &&
        scaleDiff <= 0.01 &&
        Math.abs(stage.width() - stageWidth) < 1 &&
        Math.abs(stage.height() - stageHeight) < 1
      ) {
        return;
      }
      this.orgScale = scale;

      stage.x(0);
      stage.y(0);
      stage.width(stageWidth);
      stage.height(stageHeight);
      stage.scale({ x: scale, y: scale });
      stage.batchDraw();
      this.zoomed = false;
    },
    getVariableFromTag(tag) {
      let tagVariable = tag.split(",")[1];
      return { item: tagVariable, item2: tagVariable };
    },
    scrollTargetIntoView(el) {
      if (!el) return;
      const scroller = this.$el && this.$el.closest(".card-body");
      if (scroller && scroller.contains(el)) {
        const sRect = scroller.getBoundingClientRect();
        const eRect = el.getBoundingClientRect();
        scroller.scrollTop += eRect.top - sRect.top;
      }
      if (el.scrollIntoView) el.scrollIntoView({ block: "start", inline: "nearest" });
    },
    focusGraphViewer() {
      this.$nextTick(() => {
        this.scrollTargetIntoView(this.$refs.graphViewer);
        this.$nextTick(() => {
          if (this.$refs.chartManager && this.$refs.chartManager.updateFitSize) {
            this.$refs.chartManager.updateFitSize();
          }
        });
      });
    },
    changeable(control) {
      if (control.tag.startsWith("Z") || control.tag.startsWith("POVEL"))
        return true;
      return false;
    },
    closeGraphViewer() {
      this.toDrawGraph = null;
      this.toDrawGraphVariable = null;
      this.$nextTick(() => {
        this.scrollTargetIntoView(this.$refs.viewport || this.$el);
        this.fitStage(true);
      });
    },
    setBackgroundImage() {
      if (!this.display.background) return;

      const image = new Image();
      image.onload = () => {
        this.backgroundImage = image;
        this.backgroundImageLoaded = true;
      };
      image.src = this.display.background;
    },
    getCommandImagesConfig(cmd, key) {
      var img = this.imagesOnOff.get(key);
      if (!img) {
        var imgOn = new Image();
        imgOn.src = cmd.imageWhenTrue;
        cmd.imageWhenTrue = null;

        var imgOff = new Image();
        imgOff.src = cmd.imageWhenFalse;
        cmd.imageWhenFalse = null;

        img = { off: imgOff, on: imgOn };
        this.imagesOnOff.set(key, img);
      }
      return {
        image: cmd.checked ? img.on : img.off,
        width: cmd.width,
        height: cmd.height,
        x: cmd.positionX,
        y: cmd.positionY,
        strokeEnabled: true,
        strokeWidth: cmd.checked ? 2 : 1,
        shadowBlur: cmd.checked ? 2 : 0,
        stroke: "black",
        perfectDrawEnabled: false
      };
    },
    isVisible(item) {
      return !item || item.visible !== false;
    },
    isEnabled(item) {
      return !item || item.enabled !== false;
    },
    getAlignConfig(item) {
      const fallback = { align: "center", verticalAlign: "middle" };
      const map = {
        0: { align: "left", verticalAlign: "top" },
        1: { align: "right", verticalAlign: "top" },
        2: { align: "center", verticalAlign: "top" },
        3: { align: "left", verticalAlign: "middle" },
        4: { align: "right", verticalAlign: "middle" },
        5: fallback,
        6: { align: "left", verticalAlign: "bottom" },
        7: { align: "right", verticalAlign: "bottom" },
        8: { align: "center", verticalAlign: "bottom" },
        LeftTop: { align: "left", verticalAlign: "top" },
        RightTop: { align: "right", verticalAlign: "top" },
        CenterTop: { align: "center", verticalAlign: "top" },
        LeftMid: { align: "left", verticalAlign: "middle" },
        RightMid: { align: "right", verticalAlign: "middle" },
        CenterMid: fallback,
        LeftBot: { align: "left", verticalAlign: "bottom" },
        RightBot: { align: "right", verticalAlign: "bottom" },
        CenterBot: { align: "center", verticalAlign: "bottom" }
      };
      const raw = item && item.alignment;
      if (raw === undefined || raw === null || raw === "") return fallback;
      return map[raw] || map[String(raw)] || fallback;
    },
    getGraphicConfig(g) {
      const img = new Image();
      img.src = g.imageString;
      return {
        image: img,
        width: g.width,
        height: g.height,
        x: g.positionX,
        y: g.positionY,
        stroke: "black",
        strokeEnabled: g.border,
        strokeWidth: 1,
        perfectDrawEnabled: false
      };
    },
    getTextBoxTextConfig(tb) {
      const alignment = this.getAlignConfig(tb);
      return {
        x: tb.positionX,
        y: tb.positionY,
        width: tb.width,
        height: tb.height,
        text: tb.text,
        fontFamily: tb.fontName,
        fontSize: (4 * tb.fontSize) / 3,
        fontStyle: this.getFontStyle(tb),
        align: alignment.align,
        verticalAlign: alignment.verticalAlign,
        fill: tb.frontColor,
        perfectDrawEnabled: false
      };
    },
    getCommandTextConfig(tb) {
      var cfg = this.getTextBoxTextConfig(tb);
      cfg.width -= 10;
      if (!tb.checked) cfg.x += 10;
      return cfg;
    },
    getTextBoxRectConfig(tb) {
      return {
        x: tb.positionX,
        y: tb.positionY,
        width: tb.width,
        height: tb.height,
        stroke: "black",
        strokeEnabled: tb.border,
        strokeWidth: 1,
        cornerRadius: 3,
        fill: tb.backColor,
        perfectDrawEnabled: false,
        listening: false
      };
    },
    getCommandRectConfig(tb) {
      var cfg = this.getTextBoxRectConfig(tb);
      cfg.strokeEnabled = true;
      cfg.strokeWidth = tb.checked ? 2 : 1;
      cfg.shadowBlur = tb.checked ? 2 : 0;
      return cfg;
    },
    getCommandSwitchConfig(tb) {
      var cfg = this.getTextBoxRectConfig(tb);
      cfg.fill = tb.checked ? "green" : "red";
      cfg.width = 10;
      cfg.x = tb.checked ? tb.positionX + tb.width - 10 : tb.positionX;
      cfg.strokeEnabled = true;
      cfg.strokeWidth = tb.checked ? 2 : 1;
      cfg.shadowBlur = tb.checked ? 2 : 0;
      return cfg;
    },
    showToolTip(item, cursor, extraTag = false) {
      if (
        this.isEnabled(item) &&
        (((this.changeable(item) || extraTag) && this.adminRights) || cursor)
      )
        document.body.style.cursor = "pointer";
      if (!item.toolTipText) return;
      const stage = this.$refs.stage.getStage();
      const mousePos = stage.getPointerPosition();
      const tooltipLayer = this.$refs.tooltipLayer.getStage();
      const tooltipText = this.$refs.tooltipText.getStage();
      const tooltipRect = this.$refs.tooltipRect.getStage();
      tooltipText.text(item.toolTipText);
      var x = mousePos.x;
      if (x + tooltipText.width() > stage.width()) {
        x -= x + tooltipText.width() - stage.width();
      }
      tooltipText.x(x);
      tooltipText.y(mousePos.y);

      tooltipRect.height(tooltipText.height());
      tooltipRect.width(tooltipText.width());
      tooltipRect.x(tooltipText.x());
      tooltipRect.y(tooltipText.y());

      tooltipRect.show();
      tooltipText.show();
      tooltipLayer.batchDraw();
    },
    hideToolTip() {
      const tooltipLayer = this.$refs.tooltipLayer.getStage();
      const tooltipText = this.$refs.tooltipText.getStage();
      const tooltipRect = this.$refs.tooltipRect.getStage();
      tooltipText.hide();
      tooltipRect.hide();
      tooltipLayer.batchDraw();
      document.body.style.cursor = "default";
    },
    getFontStyle(tb) {
      if (tb.fontBold || tb.fontItalic) {
        if (tb.fontBold && tb.fontItalic) return "bold italic";
        if (tb.fontBold) return "bold";
        return "italic";
      }
      return "normal";
    },
    commandPress(cmd) {
      if (!this.isEnabled(cmd)) return;
      if (cmd.checked === null || !this.adminRights) return;
      var codeNewValue = cmd.checked ? 0 : 1;
      var toSend = {
        success: true,
        value: codeNewValue,
        tag: cmd.tag
      };
      this.$emit("sendValue", toSend);
    },
    graphPress(graph) {
      if (!this.isEnabled(graph)) return;
      this.toDrawGraph = graph;
      this.toDrawGraphVariable = this.getVariableFromTag(graph.tag);
      this.focusGraphViewer();
    },
    textBoxPress(tb) {
      if (!this.isEnabled(tb) || !this.changeable(tb) || !this.adminRights) return;
      this.tbToSet = tb;
      this.tbToSet.newValue = tb.text.replace(/[^\d.-]/g, "");

      this.$refs["modalSetValue"].show();
    },
    getTagSelection(tb) {
      return getTagSelectionInput(tb);
    },
    extraTagPress(item) {
      if (!this.isEnabled(item) || !this.adminRights) return;
      let jsonData = JSON.stringify({ tag: item.tag });
      extraTagService
        .executeExtraTag(this.projectId, jsonData)
        .then(response => {
          this.$emit("showInfoError", true, `${this.$i18n.t("project.projectViewer.extraTagSuccess")}: <${response.data}>`);
        })
        .catch(error => {
          this.$emit("showInfoError", false, errorHandler(error));
        });
    },
    sendNewValue(control) {
      var codeNewValue = codeTagVariable(control.newValue);
      var toSend;
      if (codeNewValue === null)
        toSend = {
          success: false,
          value: this.$i18n.t("project.projectViewer.invalidInput"),
          tag: null
        };
      else {
        toSend = {
          success: true,
          value: codeNewValue,
          tag: control.tag
        };
        control.newValue = null;
      }
      this.$emit("sendValue", toSend);
      this.$refs["modalSetValue"].hide();
    }
  }
};
</script>

<style scoped>
.display-viewer {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.display-viewer.is-showing-graph {
  height: auto;
  min-height: 100%;
}
.stage-div {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.display-viewer.is-showing-graph .stage-div {
  flex: 0 0 auto;
}
.stage-viewport {
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}
.display-viewer.is-showing-graph .stage-viewport {
  flex: 0 0 auto;
}
.graph-viewer {
  flex-shrink: 0;
}
.mobile-canvas-hint {
  flex-shrink: 0;
  font-size: 0.8rem;
}
.stage-viewport >>> .konvajs-content {
  flex-shrink: 0;
  touch-action: pan-y;
}
.display-viewer.is-zoomed >>> .konvajs-content {
  touch-action: none;
}
.display-viewer:fullscreen,
.display-viewer:-webkit-full-screen {
  width: 100%;
  height: 100%;
  background: #fff;
  overflow: auto;
}
</style>
